import {
  IconButton,
  Tab,
  TabList,
  TabListProps,
  Tabs,
  TabsProps,
} from "@chakra-ui/react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { RiArrowLeftSLine, RiArrowRightSLine } from "react-icons/ri";

const NAV_BUTTON_WIDTH_PX = 32;
const AVG_CHAR_WIDTH_PX = 8;
const TAB_PADDING_PX = 32;

/** Returns how many consecutive tabs (starting at `offset`) fit within `available` px. */
function countFittingTabs(
  offset: number,
  available: number,
  widths: number[]
): number {
  let acc = 0;
  let n = 0;
  for (let i = offset; i < widths.length; i++) {
    const w = widths[i] ?? 0;
    if (acc + w <= available) {
      acc += w;
      n++;
    } else {
      break;
    }
  }
  return Math.max(1, n);
}

function useResizeObserver(
  ref: React.RefObject<HTMLElement>,
  onResize: () => void
) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => onResize());
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref, onResize]);
}

export type PaginatedTabListItem = {
  label: string;
  value: string;
};

export interface PaginatedTabListProps<T extends PaginatedTabListItem> {
  items: T[];
  renderTab?: (item: T, index: number) => React.ReactNode;
  currentIndex: number;
  onTabChange: (index: number) => void;
  /**
   * How many tabs to advance when paging: "page" advances by a full visible
   * page, "one" advances one tab at a time.
   * @default "one"
   */
  step?: "page" | "one";
  tabsProps?: Omit<TabsProps, "children">;
  tabListProps?: TabListProps;
}

export function PaginatedTabList<T extends PaginatedTabListItem>({
  items,
  currentIndex,
  onTabChange,
  step = "one",
  tabListProps,
  renderTab,
  tabsProps,
}: PaginatedTabListProps<T>) {
  // Imperative: visible tab list used for available-width measurement
  const listRef = useRef<HTMLDivElement>(null);

  const [tabOffset, setTabOffset] = useState(0);
  const [visibleCount, setVisibleCount] = useState(items.length);

  const estimatedTabWidths = useMemo(
    () =>
      items.map(
        (item) => item.label.length * AVG_CHAR_WIDTH_PX + TAB_PADDING_PX
      ),
    [items]
  );

  const calculateTabsPerPage = useCallback(() => {
    const listEl = listRef.current;
    if (!listEl) return;

    const containerWidth = listEl.getBoundingClientRect().width;
    const available = Math.max(0, containerWidth - NAV_BUTTON_WIDTH_PX * 2);

    // Reads current offset via updater to keep the callback stable
    setTabOffset((currentOffset) => {
      setVisibleCount(
        countFittingTabs(currentOffset, available, estimatedTabWidths)
      );
      return currentOffset;
    });
  }, [estimatedTabWidths]);

  useResizeObserver(listRef, calculateTabsPerPage);

  useEffect(() => {
    calculateTabsPerPage();
  }, [calculateTabsPerPage]);

  const endExclusive = Math.min(items.length, tabOffset + visibleCount);
  const hasPrevTabs = tabOffset > 0;
  const hasNextTabs = endExclusive < items.length;
  const stepSize = step === "one" ? 1 : visibleCount;

  const visibleTabs = useMemo(
    () => items.slice(tabOffset, endExclusive),
    [items, tabOffset, endExclusive]
  );

  // When currentIndex changes, adjust tabOffset so the selected tab is visible.
  // Reads tabOffset via the state updater to avoid a dependency cycle.
  useEffect(() => {
    const listEl = listRef.current;
    if (!listEl) return;

    const containerWidth = listEl.getBoundingClientRect().width;
    const available = Math.max(0, containerWidth - NAV_BUTTON_WIDTH_PX * 2);

    setTabOffset((currentOffset) => {
      if (currentIndex < currentOffset) {
        return currentIndex;
      }

      const count = countFittingTabs(
        currentOffset,
        available,
        estimatedTabWidths
      );

      if (currentIndex >= currentOffset + count) {
        return Math.max(0, currentIndex - count + 1);
      }

      return currentOffset;
    });
  }, [currentIndex, estimatedTabWidths]);

  // Calculate the index of the current tab in the visible range
  const visibleTabIndex = useMemo(() => {
    if (
      currentIndex >= tabOffset &&
      currentIndex < tabOffset + visibleTabs.length
    ) {
      return currentIndex - tabOffset;
    }
    return 0;
  }, [currentIndex, tabOffset, visibleTabs.length]);

  const handleVisibleTabChange = useCallback(
    (index: number) => {
      onTabChange(tabOffset + index);
    },
    [onTabChange, tabOffset]
  );

  const handleNextTabs = useCallback(
    () =>
      setTabOffset((s) =>
        Math.min(Math.max(0, items.length - 1), s + stepSize)
      ),
    [items.length, stepSize]
  );

  const handlePrevTabs = useCallback(
    () => setTabOffset((s) => Math.max(0, s - stepSize)),
    [stepSize]
  );

  return (
    <Tabs
      index={visibleTabIndex}
      onChange={handleVisibleTabChange}
      colorScheme="primary"
      {...tabsProps}
    >
      <TabList
        ref={listRef}
        width="100%"
        alignItems="flex-start"
        flex="1"
        minW={0}
        {...tabListProps}
      >
        {hasPrevTabs && (
          <IconButton
            aria-label="Previous tabs"
            icon={<RiArrowLeftSLine />}
            variant="ghost"
            size="sm"
            onClick={handlePrevTabs}
          />
        )}
        {visibleTabs.map((item, index) => (
          <Tab key={item.value}>
            {renderTab ? renderTab(item, index + tabOffset) : item.label}
          </Tab>
        ))}
        {hasNextTabs && (
          <IconButton
            aria-label="Next tabs"
            icon={<RiArrowRightSLine />}
            variant="ghost"
            size="sm"
            onClick={handleNextTabs}
          />
        )}
      </TabList>
    </Tabs>
  );
}
